import { useState } from "react";

export function formatTimestamp(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  const sInt = Math.floor(s);
  const dec = Math.round((s - sInt) * 10);
  const secStr = String(sInt).padStart(2, "0");
  return dec > 0 ? `${m}:${secStr}.${dec}` : `${m}:${secStr}`;
}

function buildDisplay(intDigits: string, hasDecimal: boolean, decDigits: string): string {
  const secStr = intDigits.length >= 2 ? intDigits.slice(-2) : intDigits.padStart(2, "0");
  const minStr = intDigits.length > 2 ? String(Number.parseInt(intDigits.slice(0, -2), 10)) : "0";
  const base = `${minStr}:${secStr}`;
  return hasDecimal ? `${base}.${decDigits}` : base;
}

function computeSeconds(intDigits: string, hasDecimal: boolean, decDigits: string): number {
  const d = intDigits || "0";
  const sec = Number.parseInt(d.slice(-2).padStart(2, "0"), 10);
  const min = d.length > 2 ? Number.parseInt(d.slice(0, -2), 10) : 0;
  const dec = hasDecimal && decDigits ? Number.parseFloat(`0.${decDigits}`) : 0;
  return min * 60 + sec + dec;
}

export function TimeInput({
  value,
  otherTimestamps,
  onChange,
  errorMessage,
  onFocus,
}: {
  value: number;
  otherTimestamps: number[];
  onChange: (seconds: number) => void;
  errorMessage?: string | undefined;
  onFocus?: () => void;
}) {
  const [intDigits, setIntDigits] = useState("");
  const [decDigits, setDecDigits] = useState("");
  const [hasDecimal, setHasDecimal] = useState(false);
  const [hasTouched, setHasTouched] = useState(false);

  const hasInput = hasTouched && (intDigits.length > 0 || hasDecimal);
  const pendingSeconds = hasInput ? computeSeconds(intDigits, hasDecimal, decDigits) : value;
  const isDuplicate = hasInput && otherTimestamps.includes(pendingSeconds);
  const display = hasInput
    ? buildDisplay(intDigits, hasDecimal, decDigits)
    : formatTimestamp(value);

  const handleFocus = () => {
    onFocus?.();
    setHasTouched(false);
    setIntDigits("");
    setDecDigits("");
    setHasDecimal(false);
  };

  const handleBlur = () => {
    if (hasInput && !isDuplicate) {
      onChange(pendingSeconds);
    }
    setHasTouched(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key >= "0" && e.key <= "9") {
      e.preventDefault();
      setHasTouched(true);
      if (!hasDecimal) {
        setIntDigits((prev) => prev + e.key);
      } else {
        setDecDigits((prev) => prev + e.key);
      }
    } else if (e.key === "," || e.key === ".") {
      e.preventDefault();
      setHasTouched(true);
      setHasDecimal(true);
    } else if (e.key === "Backspace") {
      e.preventDefault();
      if (decDigits.length > 0) {
        setDecDigits((prev) => prev.slice(0, -1));
      } else if (hasDecimal) {
        setHasDecimal(false);
      } else {
        setIntDigits((prev) => prev.slice(0, -1));
      }
    }
  };

  return (
    <div className="relative">
      <input
        type="text"
        inputMode="decimal"
        value={display}
        onChange={() => {}}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={`w-16 px-2 py-1 text-xs font-mono font-semibold bg-surface border rounded outline-none text-center cursor-text transition-colors ${
          isDuplicate || errorMessage
            ? "border-red-400 ring-1 ring-red-100 text-red-500"
            : "border-border focus:border-blue-400 focus:ring-1 focus:ring-blue-100"
        }`}
      />
      {(isDuplicate || errorMessage) && (
        <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1 whitespace-nowrap text-[9px] font-semibold text-red-500 bg-white border border-red-200 px-1.5 py-0.5 rounded shadow-sm z-10">
          {errorMessage || "Timestamp taken"}
        </span>
      )}
    </div>
  );
}
